import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [balance, setBalance] = useState(0);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Load payment data when user changes
  useEffect(() => {
    if (user) {
      loadPaymentData();
    } else {
      // Clear payment data when user logs out
      setBalance(0);
      setPaymentMethods([]);
      setTransactions([]);
    }
  }, [user]);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API calls
      // Simulate API calls
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock data
      const mockBalance = 1250.75;
      const mockPaymentMethods = [
        {
          id: '1',
          type: 'credit_card',
          brand: 'visa',
          last4: '4242',
          expiry: '12/25',
          isDefault: true,
          holderName: 'Juan Pérez'
        },
        {
          id: '2',
          type: 'paypal',
          email: 'juan@empresa.com',
          isDefault: false
        }
      ];
      const mockTransactions = [
        {
          id: '1',
          type: 'deposit',
          amount: 1500.00,
          description: 'Recarga de saldo',
          date: new Date('2025-01-15').toISOString(),
          status: 'completed',
          method: 'Visa •••• 4242'
        },
        {
          id: '2',
          type: 'payment',
          amount: -249.25,
          description: 'Envío FedEx - Orden #12345',
          date: new Date('2025-01-14').toISOString(),
          status: 'completed',
          method: 'Saldo de cuenta'
        },
        {
          id: '3',
          type: 'deposit',
          amount: 1000.00,
          description: 'Recarga de saldo',
          date: new Date('2025-01-10').toISOString(),
          status: 'completed',
          method: 'PayPal'
        }
      ];

      setBalance(mockBalance);
      setPaymentMethods(mockPaymentMethods);
      setTransactions(mockTransactions);
    } catch (error) {
      setError(error.message || 'Error loading payment data');
    } finally {
      setLoading(false);
    }
  };

  const addPaymentMethod = async (paymentData) => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Mock successful addition
      const newPaymentMethod = {
        id: Date.now().toString(),
        ...paymentData,
        isDefault: paymentMethods.length === 0
      };

      setPaymentMethods(prev => [...prev, newPaymentMethod]);
      return { success: true, paymentMethod: newPaymentMethod };
    } catch (error) {
      setError(error.message || 'Error adding payment method');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const removePaymentMethod = async (paymentMethodId) => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setPaymentMethods(prev => prev.filter(method => method.id !== paymentMethodId));
      return { success: true };
    } catch (error) {
      setError(error.message || 'Error removing payment method');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const setDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 800));

      setPaymentMethods(prev => prev.map(method => ({
        ...method,
        isDefault: method.id === paymentMethodId
      })));
      return { success: true };
    } catch (error) {
      setError(error.message || 'Error setting default payment method');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const rechargeBalance = async (amount, paymentMethodId) => {
    try {
      setLoading(true);
      setError(null);

      // Validate minimum amount
      if (amount < 350) {
        throw new Error('El monto mínimo de recarga es $350 pesos');
      }

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      const paymentMethod = paymentMethods.find(method => method.id === paymentMethodId);
      const methodDescription = paymentMethod ? 
        (paymentMethod.type === 'credit_card' ? 
          `${paymentMethod.brand} •••• ${paymentMethod.last4}` : 
          paymentMethod.email) : 
        'Método de pago';

      // Create transaction record
      const newTransaction = {
        id: Date.now().toString(),
        type: 'deposit',
        amount: amount,
        description: 'Recarga de saldo',
        date: new Date().toISOString(),
        status: 'completed',
        method: methodDescription
      };

      // Update balance and transactions
      setBalance(prev => prev + amount);
      setTransactions(prev => [newTransaction, ...prev]);

      return { success: true, transaction: newTransaction };
    } catch (error) {
      setError(error.message || 'Error recharging balance');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async (amount, description) => {
    try {
      setLoading(true);
      setError(null);

      // Check sufficient balance
      if (balance < amount) {
        throw new Error('Saldo insuficiente. Recarga tu cuenta para continuar.');
      }

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Create transaction record
      const newTransaction = {
        id: Date.now().toString(),
        type: 'payment',
        amount: -amount,
        description: description,
        date: new Date().toISOString(),
        status: 'completed',
        method: 'Saldo de cuenta'
      };

      // Update balance and transactions
      setBalance(prev => prev - amount);
      setTransactions(prev => [newTransaction, ...prev]);

      return { success: true, transaction: newTransaction };
    } catch (error) {
      setError(error.message || 'Error processing payment');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const generateInvoice = async (transactionId) => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock invoice generation
      const invoice = {
        id: `INV-${Date.now()}`,
        transactionId: transactionId,
        downloadUrl: `/api/invoices/INV-${Date.now()}.pdf`,
        generatedAt: new Date().toISOString()
      };

      return { success: true, invoice };
    } catch (error) {
      setError(error.message || 'Error generating invoice');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    balance,
    paymentMethods,
    transactions,
    loading,
    error,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
    rechargeBalance,
    processPayment,
    generateInvoice,
    clearError,
    refreshPaymentData: loadPaymentData
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContext;